import React, { useState, useEffect } from 'react';
import { Download, FileSpreadsheet, Users, FileText, Activity, CreditCard, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { membersAPI } from '../services/apiService';
import { claimsAPI } from '../services/apiService';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [claims, setClaims] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membersData, claimsData] = await Promise.all([
        membersAPI.getAll(),
        claimsAPI.getAll()
      ]);
      setMembers(membersData);
      setClaims(claimsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data for reports');
    } finally {
      setLoading(false);
    }
  };

  // Convert array of objects to CSV string
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    // Get headers from first object
    const headers = Object.keys(data[0]);
    
    // Create CSV header row
    const headerRow = headers.map(header => `"${String(header).replace(/"/g, '""')}"`).join(',');
    
    // Create CSV data rows
    const dataRows = data.map(row => {
      return headers.map(header => {
        const value = row[header];
        // Handle null/undefined
        if (value === null || value === undefined) return '""';
        // Convert to string and escape quotes
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      }).join(',');
    });
    
    // Combine header and data rows
    return [headerRow, ...dataRows].join('\n');
  };

  // Download CSV file
  const downloadCSV = (csvContent, filename) => {
    // Add BOM for UTF-8 to support special characters in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToExcel = (data, filename, sheetName = 'Sheet1') => {
    try {
      if (!data || data.length === 0) {
        toast.error('No data available to export');
        return;
      }
      
      const csvContent = convertToCSV(data);
      downloadCSV(csvContent, filename);
      toast.success(`${filename} exported successfully! (CSV format - can be opened in Excel)`);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast.error('Failed to export data');
    }
  };

  const exportMemberList = () => {
    const memberData = members.map(member => ({
      'SL No': member.sl_no || '',
      'Name': member.name || '',
      'UAN': member.uan || '',
      'Phone Number': member.phone_number || '',
      'WhatsApp': member.whatsapp || '',
      'Referred By': member.referred_by || '',
      'Created At': member.created_at ? new Date(member.created_at).toLocaleDateString() : '',
      'Updated At': member.updated_at ? new Date(member.updated_at).toLocaleDateString() : ''
    }));
    exportToExcel(memberData, 'Member_List', 'Members');
  };

  const exportClaimList = () => {
    const claimData = claims.map(claim => ({
      'Claim ID': claim.id || '',
      'Member Name': claim.member_name || '',
      'UAN': claim.uan || '',
      'SL No': claim.sl_no || '',
      'Requirements': Array.isArray(claim.requirements) ? claim.requirements.join(', ') : (claim.requirement || ''),
      'Final Status': claim.final_status || claim.status || '',
      'PF Balance': claim.pf_balance || 0,
      'EPS Balance': claim.eps_balance || 0,
      'Total Balance': claim.total_balance || 0,
      'Fees': claim.fees || 0,
      'Advance': claim.advance || 0,
      'Balance': claim.balance || 0,
      'Remarks': claim.remarks || '',
      'Created At': claim.created_at ? new Date(claim.created_at).toLocaleDateString() : '',
      'Updated At': claim.updated_at ? new Date(claim.updated_at).toLocaleDateString() : ''
    }));
    exportToExcel(claimData, 'Claim_List', 'Claims');
  };

  const exportClaimStatus = () => {
    const statusData = claims.map(claim => {
      const baseData = {
        'Claim ID': claim.id || '',
        'Member Name': claim.member_name || '',
        'UAN': claim.uan || '',
        'SL No': claim.sl_no || '',
        'Final Status': claim.final_status || claim.status || '',
        'PF Activation': claim.pf_activation_status || 'Not Started',
        'Joint Declaration Filing': claim.joint_declaration_filing_status || 'Not Started',
        'KYC Status': claim.kyc_status || claim.kyc_updation_status || 'Not Started',
        'E-Nomination': claim.e_nomination_status || 'Not Started',
        'Contribution Adjustment': claim.contribution_adjustment_status || 'Not Started',
        'Transfers': claim.transfers_status || 'Not Started',
        'Advance Claim': claim.advance_claim_status || claim.pf_advance_claim_status || 'Not Started',
        'PF Final Settlement': claim.pf_final_settlement_status || 'Not Started',
        'Scheme Certificate': claim.scheme_certificate_status || 'Not Started',
        'Monthly Pension Application': claim.monthly_pension_application_status || 'Not Started',
        'EDLI Claim': claim.death_claim_pf_edli_eps_application_status || 'Not Started',
        'Remarks': claim.remarks || '',
        'Updated At': claim.updated_at ? new Date(claim.updated_at).toLocaleDateString() : ''
      };
      return baseData;
    });
    exportToExcel(statusData, 'Claim_Status_Report', 'Claim Status');
  };

  const exportActivityStatus = () => {
    // Get all unique activities across all claims
    const activities = [
      'PF Activation',
      'Joint Declaration Filing',
      'KYC Status',
      'E-Nomination',
      'Contribution Adjustment',
      'Transfers',
      'Advance Claim',
      'PF Final Settlement',
      'Scheme Certificate',
      'Monthly Pension Application',
      'EDLI Claim'
    ];

    const activityData = [];
    
    claims.forEach(claim => {
      activities.forEach(activity => {
        let status = 'Not Started';
        let fieldName = '';
        
        switch(activity) {
          case 'PF Activation':
            status = claim.pf_activation_status || 'Not Started';
            fieldName = 'pf_activation_status';
            break;
          case 'Joint Declaration Filing':
            status = claim.joint_declaration_filing_status || 'Not Started';
            fieldName = 'joint_declaration_filing_status';
            break;
          case 'KYC Status':
            status = claim.kyc_status || claim.kyc_updation_status || 'Not Started';
            fieldName = 'kyc_status';
            break;
          case 'E-Nomination':
            status = claim.e_nomination_status || 'Not Started';
            fieldName = 'e_nomination_status';
            break;
          case 'Contribution Adjustment':
            status = claim.contribution_adjustment_status || 'Not Started';
            fieldName = 'contribution_adjustment_status';
            break;
          case 'Transfers':
            status = claim.transfers_status || 'Not Started';
            fieldName = 'transfers_status';
            break;
          case 'Advance Claim':
            status = claim.advance_claim_status || claim.pf_advance_claim_status || 'Not Started';
            fieldName = 'advance_claim_status';
            break;
          case 'PF Final Settlement':
            status = claim.pf_final_settlement_status || 'Not Started';
            fieldName = 'pf_final_settlement_status';
            break;
          case 'Scheme Certificate':
            status = claim.scheme_certificate_status || 'Not Started';
            fieldName = 'scheme_certificate_status';
            break;
          case 'Monthly Pension Application':
            status = claim.monthly_pension_application_status || 'Not Started';
            fieldName = 'monthly_pension_application_status';
            break;
          case 'EDLI Claim':
            status = claim.death_claim_pf_edli_eps_application_status || 'Not Started';
            fieldName = 'death_claim_pf_edli_eps_application_status';
            break;
        }

        // Only include if the claim has this requirement selected
        const requirements = Array.isArray(claim.requirements) ? claim.requirements : 
                            (claim.requirement ? claim.requirement.split(', ').map(r => r.trim()) : []);
        
        const activityNameMap = {
          'PF Activation': ['PF Activation', 'Activation'],
          'Joint Declaration Filing': ['Joint Declaration Filing', 'JD Issues (No.)'],
          'KYC Status': ['KYC Updation', 'KYC Issues (No.)'],
          'E-Nomination': ['E-Nomination', 'E-Nomination Filing', 'e-Nomination'],
          'Contribution Adjustment': ['Contribution Adjustment'],
          'Transfers': ['Transfers', 'Transfers (No.)'],
          'Advance Claim': ['Advance Claim', 'PF Advance Claim', 'Advance Claims (Form-31)'],
          'PF Final Settlement': ['PF Final Settlement', 'PF Final Settlement (Form-19)'],
          'Scheme Certificate': ['Scheme Certificate', 'Scheme Certificate Application', 'Scheme Certificate (Form-10C)'],
          'Monthly Pension Application': ['Monthly Pension application', 'Monthly Pension Application', 'Pension Claim (Form-10D)'],
          'EDLI Claim': ['EDLI Claim', 'Death Claim Handling', 'Death Claim - PF, EDLI & EPS application']
        };

        const requirementNames = activityNameMap[activity] || [activity];
        if (requirementNames.some((n) => requirements.includes(n)) || requirements.length === 0) {
          activityData.push({
            'Claim ID': claim.id || '',
            'Member Name': claim.member_name || '',
            'UAN': claim.uan || '',
            'Activity': activity,
            'Status': status,
            'Updated At': claim.updated_at ? new Date(claim.updated_at).toLocaleDateString() : ''
          });
        }
      });
    });

    exportToExcel(activityData, 'Activity_Status_Report', 'Activity Status');
  };

  const exportPaymentStatus = () => {
    const paymentData = [];
    
    claims.forEach(claim => {
      let payments = [];
      
      // Handle payments - could be array, JSON string, or undefined
      if (claim.payments) {
        if (Array.isArray(claim.payments)) {
          payments = claim.payments;
        } else if (typeof claim.payments === 'string') {
          try {
            payments = JSON.parse(claim.payments);
            if (!Array.isArray(payments)) {
              payments = [];
            }
          } catch (e) {
            console.warn('Failed to parse payments:', e);
            payments = [];
          }
        }
      }
      
      if (payments.length > 0) {
        payments.forEach((payment, index) => {
          paymentData.push({
            'Claim ID': claim.id || '',
            'Member Name': claim.member_name || '',
            'UAN': claim.uan || '',
            'Payment #': index + 1,
            'Amount': parseFloat(payment.amount) || 0,
            'Payment Date': payment.date || '',
            'Total Fees': claim.fees || 0,
            'Total Advance': claim.advance || 0,
            'Balance': claim.balance || 0,
            'Payment Date (Formatted)': payment.date ? new Date(payment.date).toLocaleDateString() : ''
          });
        });
      } else {
        // If no payments array, show advance if exists
        if (claim.advance && parseFloat(claim.advance) > 0) {
          paymentData.push({
            'Claim ID': claim.id || '',
            'Member Name': claim.member_name || '',
            'UAN': claim.uan || '',
            'Payment #': 1,
            'Amount': parseFloat(claim.advance) || 0,
            'Payment Date': '',
            'Total Fees': claim.fees || 0,
            'Total Advance': claim.advance || 0,
            'Balance': claim.balance || 0,
            'Payment Date (Formatted)': ''
          });
        }
      }
    });

    if (paymentData.length === 0) {
      toast.error('No payment data available to export');
      return;
    }

    exportToExcel(paymentData, 'Payment_Status_Report', 'Payments');
  };

  const exportServiceHistory = () => {
    const serviceData = [];
    
    claims.forEach(claim => {
      let serviceHistory = [];
      
      // Handle service_history - could be array, JSON string, or undefined
      if (claim.service_history) {
        if (Array.isArray(claim.service_history)) {
          serviceHistory = claim.service_history;
        } else if (typeof claim.service_history === 'string') {
          try {
            serviceHistory = JSON.parse(claim.service_history);
            if (!Array.isArray(serviceHistory)) {
              serviceHistory = [];
            }
          } catch (e) {
            console.warn('Failed to parse service_history:', e);
            serviceHistory = [];
          }
        }
      }
      
      if (serviceHistory.length > 0) {
        serviceHistory.forEach((entry, index) => {
          serviceData.push({
            'Claim ID': claim.id || '',
            'Member Name': claim.member_name || '',
            'UAN': claim.uan || '',
            'Entry #': index + 1,
            'Company Name': entry.company_name || '',
            'Member ID': entry.member_id || '',
            'Date of Joining (DOJ)': entry.doj || '',
            'Date of Leaving (DOL)': entry.dol || '',
            'Service Duration': entry.service || '',
            'PF+EPF Balance': entry.pf_epf_balance || 0,
            'EPS Balance': entry.eps_balance || 0,
            'Form-31': entry.form_31 || '',
            'Form-19': entry.form_19 || '',
            'Form-10C/Scheme Certificate': entry.form_10c_scheme_certificate || ''
          });
        });
      }
    });

    if (serviceData.length === 0) {
      toast.error('No service history data available to export');
      return;
    }

    exportToExcel(serviceData, 'Service_History_Report', 'Service History');
  };

  const reportCards = [
    {
      id: 'member-list',
      title: 'Member List',
      description: 'Export all member details including name, UAN, contact information',
      icon: Users,
      color: 'bg-blue-500',
      onClick: exportMemberList
    },
    {
      id: 'claim-list',
      title: 'Claim List',
      description: 'Export all claim details with member information and balances',
      icon: FileText,
      color: 'bg-green-500',
      onClick: exportClaimList
    },
    {
      id: 'claim-status',
      title: 'Claim Status',
      description: 'Export claim status with all activity statuses',
      icon: Activity,
      color: 'bg-purple-500',
      onClick: exportClaimStatus
    },
    {
      id: 'activity-status',
      title: 'Activity Status',
      description: 'Export detailed activity-wise status for all claims',
      icon: Activity,
      color: 'bg-orange-500',
      onClick: exportActivityStatus
    },
    {
      id: 'payment-status',
      title: 'Payment Status',
      description: 'Export payment details including amounts and dates',
      icon: CreditCard,
      color: 'bg-indigo-500',
      onClick: exportPaymentStatus
    },
    {
      id: 'service-history',
      title: 'Service History',
      description: 'Export service history details for all claims',
      icon: FileSpreadsheet,
      color: 'bg-pink-500',
      onClick: exportServiceHistory
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Excel Reports</h1>
          <p className="mt-2 text-sm text-gray-600">
            Generate and download Excel reports for various data
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Refreshing...
            </>
          ) : (
            'Refresh Data'
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reportCards.map((report) => {
          const Icon = report.icon;
          return (
            <div
              key={report.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className={`${report.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {report.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {report.description}
                  </p>
                </div>
              </div>
              <button
                onClick={report.onClick}
                disabled={loading}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export to Excel
              </button>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Total Members</span>
            <span className="text-2xl font-bold text-gray-900">{members.length}</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Total Claims</span>
            <span className="text-2xl font-bold text-gray-900">{claims.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
